import React, { useState, useEffect, forwardRef } from 'react'
import { Input, Select, Button, formatSelectOptions } from '../../../components'
import { useMessage } from '../../../hooks'
import { candidateService } from '../services'
import { MILESTONE_TYPES } from '../helpers/milestones'

const emptyRow = () => ({ name: '', email: '', phone: '', linkedin: '', ownerUserId: '', source: '', notes: '' })

const _CandidateBulkForm = ({ submitForm, loading, setLoading }, ref) => {

  const [ownerUserId, setOwnerUserId] = useState('')
  const [source, setSource] = useState('')
  const [initialMilestone, setInitialMilestone] = useState('potential')
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()])
  const [users, setUsers] = useState([])
  const [invalid, setInvalid] = useState({})

  const { showMessage } = useMessage()

  useEffect(() => {
    candidateService.getUsers()
      .then(res => setUsers(res.users || []))
      .catch(err => showMessage(JSON.stringify(err), 'error'))
  }, [])

  const setRow = (index, field, value) => {
    setInvalid(prev => { const next = { ...prev }; delete next[`${index}.${field}`]; return next })
    setRows(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }
  const addRow = () => setRows(prev => [...prev, emptyRow()])
  const removeRow = (index) => setRows(prev => prev.filter((_, i) => i !== index))

  const onSubmit = (event) => {
    event.preventDefault()

    const invalidForm = {}
    if (!ownerUserId) invalidForm.owner_user_id = { message: 'Du måste välja en ägare' }

    const nonEmptyRows = rows.filter(r => r.name.trim() || r.email.trim() || r.phone.trim() || r.linkedin.trim())
    if (!nonEmptyRows.length) invalidForm.rows = { message: 'Lägg till minst en kandidat med namn' }
    nonEmptyRows.forEach((r, i) => { if (!r.name.trim()) invalidForm[`${i}.name`] = { message: `Rad ${i + 1} saknar namn` } })

    if (Object.keys(invalidForm).length > 0) {
      setInvalid(invalidForm)
      return
    }

    setLoading(true)
    submitForm({
      owner_user_id: Number(ownerUserId),
      source: source.trim() || undefined,
      initial_milestone: initialMilestone,
      rows: nonEmptyRows.map(r => ({
        name:          r.name.trim(),
        email:         r.email.trim()    || undefined,
        phone:         r.phone.trim()    || undefined,
        linkedin:      r.linkedin.trim() || undefined,
        owner_user_id: r.ownerUserId ? Number(r.ownerUserId) : undefined,
        source:        r.source.trim()   || undefined,
        notes:         r.notes.trim()    || undefined,
      })),
    })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>

        <p style={{ color: 'var(--color-dark-grey)', marginBottom: 16 }}>
          En rad per kandidat. Ägare, källa och startskede gäller för hela batchen.
        </p>

        <div className="form-block vertical">
          <label>Ägare (för hela batchen)</label>
          <Select
            name="owner_user_id"
            value={ownerUserId}
            options={formatSelectOptions({ options: users, optionValue: 'user_id', optionLabel: 'name', tooltip: 'email' })}
            onChange={e => { setOwnerUserId(e.value); setInvalid(prev => { const n = { ...prev }; delete n.owner_user_id; return n }) }}
            disabled={loading}
            invalid={invalid.owner_user_id}
            placeholder="Välj ägare…"
          />
        </div>

        <div className="form-block vertical">
          <Input type="text" name="source" label="Källa (för hela batchen)" value={source} onChange={e => setSource(e.target.value)} disabled={loading} />
        </div>

        <div className="form-block vertical">
          <label>Startskede</label>
          <Select
            name="initial_milestone"
            value={initialMilestone}
            options={formatSelectOptions({ options: MILESTONE_TYPES, optionValue: 'value', optionLabel: 'label' })}
            onChange={e => setInitialMilestone(e.value)}
            disabled={loading}
          />
        </div>

        <div className="form-block vertical">
          <label>Kandidater</label>
          {invalid.rows && <p style={{ color: 'var(--color-intense-red)', fontSize: '0.85em' }}>{invalid.rows.message}</p>}

          {rows.map((r, i) => (
            <div className="cd-bulk-row" key={i}>
              <div className="cd-bulk-row-header">
                <span>{r.name || `Kandidat ${i + 1}`}</span>
                {rows.length > 1 && <button type="button" className="cd-plain-remove" onClick={() => removeRow(i)}>Ta bort</button>}
              </div>
              <div className="cd-bulk-row-fields">
                <Input type="text" name={`name-${i}`} label="Namn" value={r.name} onChange={e => setRow(i, 'name', e.target.value)} disabled={loading} invalid={invalid[`${i}.name`]} />
                <Input type="text" name={`email-${i}`} label="Email" value={r.email} onChange={e => setRow(i, 'email', e.target.value)} disabled={loading} />
                <Input type="text" name={`phone-${i}`} label="Telefon" value={r.phone} onChange={e => setRow(i, 'phone', e.target.value)} disabled={loading} />
                <Input type="text" name={`linkedin-${i}`} label="LinkedIn" value={r.linkedin} onChange={e => setRow(i, 'linkedin', e.target.value)} disabled={loading} />

                <div className="cd-bulk-row-overrides">
                  <Select
                    name={`owner-${i}`}
                    label="Ägare (avvikande)"
                    value={r.ownerUserId}
                    options={formatSelectOptions({ options: users, optionValue: 'user_id', optionLabel: 'name', tooltip: 'email' })}
                    onChange={e => setRow(i, 'ownerUserId', e.value)}
                    disabled={loading}
                    isClearable
                    placeholder="Ärver batchens ägare"
                  />
                  <Input type="text" name={`source-${i}`} label="Källa (avvikande)" value={r.source} onChange={e => setRow(i, 'source', e.target.value)} disabled={loading} placeholder="Ärver batchens källa" />
                </div>

                <div className="form-block vertical">
                  <label>Anteckningar</label>
                  <textarea
                    className="cd-plain-textarea"
                    name={`notes-${i}`}
                    rows={2}
                    value={r.notes}
                    onChange={e => setRow(i, 'notes', e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          ))}
          <Button value="+ Lägg till rad" className="button" onClick={addRow} disabled={loading} />
        </div>

      </form>
    </div>
  )
}

export const CandidateBulkForm = forwardRef(_CandidateBulkForm)
